import {
    Action,
    Module,
    Mutation,
    VuexModule,
    getModule,
} from 'vuex-module-decorators';
import { valueIncludesFilterValue } from '~/helpers';
import { FilteredUserLogData, UserLogData, UserLogsState } from '~/interfaces/userLogs.interface';
import apiService from '~/services/api.service';
import { UserModule } from '~/store/modules/user';
import store from '~common-store';
import { LoadingModule } from '~common-store/modules/loading';
import { PopupModule } from '~common-store/modules/popup';

@Module({ dynamic: true, store, name: 'userLogs', namespaced: true })
class UserLogs extends VuexModule implements UserLogsState {
    logs: UserLogData[] = [];

    get logsDisplayed() {
        if (this.logs.length === 0) {
            return [];
        }
        let filteredLogs: FilteredUserLogData[] = this.mapLogs(this.logs);
        filteredLogs = this.filterLogs(filteredLogs);
        return filteredLogs;
    }

    get mapLogs() {
        return (logs: UserLogData[]) => {
            return logs.map((log) => {
                let homeId: string | { $id: string } | undefined = log.home_id;
                let userId: string | { $id: string } | undefined = log.user_id;
                const deviceId: string | string[] | undefined = log.device_id;
                const applicationName: string | undefined = log.application_name;
                if (homeId) {
                    homeId = homeId.$id;
                }
                if (userId) {
                    if ((userId as { $id: string }).$id) {
                        userId = (userId as { $id: string }).$id;
                    }
                }
                return {
                    action: log.action,
                    values: { homeId, userId, deviceId, applicationName },
                    date: log.date,
                } as FilteredUserLogData;
            }).reverse();
        };
    }

    get filterLogs() {
        // function that filters logs depending on the filter value and if we want to include or exclude it
        return (logs: FilteredUserLogData[]) => {
            return logs.filter((log) => {
                const logValues = log.values;

                const isActionIncluded = valueIncludesFilterValue(log.action);
                let isHomeIdIncluded = false;
                let isUserIdIncluded = false;
                let isDeviceIdIncluded = false;

                if (logValues.homeId) {
                    isHomeIdIncluded = valueIncludesFilterValue(logValues.homeId);
                }
                if (logValues.userId) {
                    isUserIdIncluded = valueIncludesFilterValue(logValues.userId);
                }

                // deviceId can be an array of strings OR a simple string
                if (logValues.deviceId) {
                    // if it is an array, iterate over its strings
                    if (Array.isArray(logValues.deviceId)) {
                        for (const deviceId of logValues.deviceId) {
                            isDeviceIdIncluded = valueIncludesFilterValue(deviceId);
                            // if any of the strings include the filter value, set isDeviceIncluded to true and break
                            // the loop (we only need to know if one of them includes the filter value)
                            if (isDeviceIdIncluded) {
                                break;
                            }
                        }
                    } else {
                        isDeviceIdIncluded = valueIncludesFilterValue(logValues.deviceId);
                    }
                }

                // return true if filter value is falsy (empty string) or check if it is included in any of the value
                // and if we want to show logs including or excluding the filter value
                return !PopupModule.filterValue
                    || (
                        PopupModule.filterIncludesValue
                        && (isActionIncluded || isHomeIdIncluded || isUserIdIncluded || isDeviceIdIncluded)
                    )
                    || (
                        !PopupModule.filterIncludesValue
                        && !(isActionIncluded || isHomeIdIncluded || isUserIdIncluded || isDeviceIdIncluded)
                    );

            });
        };
    }

    @Action({ commit: 'SET_USER_LOGS' })
    async getUserLogs() {
        try {
            LoadingModule.showLoading(true);
            const response = await apiService.admingetuserlogs({ user_id: UserModule.id });
            LoadingModule.showLoading(false);
            return response.data.body;
        } catch (error) {
            return [];
        } finally {
            PopupModule.setLastExecuted(new Date());
        }
    }

    @Mutation
    SET_USER_LOGS(logs: UserLogData[]) {
        this.logs = logs;
    }

}

export const UserLogsModule = getModule(UserLogs);
